import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Grid } from '@mui/material';
import * as Yup from 'yup';
import { useSaveData } from '../../../hooks/useSaveData';
import { useReactHookForm } from '../../../hooks/useReactHookForm';
import { officeApiKeyStore } from '../../../api/office-api-key-store';
import { ReactHookForm } from '../../../components/hook-form/react-hook-form.component';
import { OfficeInfoForm } from './office-info.form';
import { OfficeContactInfoForm } from './office-contact-info.form';
import { ContactsForm } from './contacts.form';
import { BillingDetailsForm } from './billing-details.form';
import { ServicesForm } from './services.form';
import { FormButtons } from './form-buttons.component';
import { Office } from '../../../models/office';
import { NotesDialog } from '../../note/notes.dialog';
import { ContractsForm } from './contracts.form';
import { FeaturesSetup } from './features-setup.component';
import { UsersRoleComponent } from './user-role-list.component';
import { CustomerDropdown } from './customer-dropdown.component';
import { FileUpload } from './file-upload/file-upload.component';
import { GuardedComponent } from '../../../components/guarded-component';
import { UserPermissionName } from '../../../models/user-permission-name';
import { useLoadData } from '../../../hooks/useLoadData';
import { ApiResponse } from '../../../models/api-response';
import { Service } from '../../../models/masters/service';
import { servicesApiKeyStore } from '../../../api/masters/services-api-key-store';
import { customersApiKeyStore } from '../../../api/customers-api-key-store';
import { useSnackbar } from '../../../hooks/use-snackbar.hook';
import { parseErrorMessageRecursive } from '../../../utils/yup-error-parser.util';

const styling = {
  root: { paddingRight: '15px', paddingBottom: '10px' },
};

interface SaveOfficeComponentProps {
  customerID?: number;
  office?: Office;
  page?: string;
}

const isUATEnable = process.env.REACT_APP_UAT_FLAG === 'false';

const officeValidationSchema = Yup.object().shape({
  customer: Yup.object().shape({ customerID: Yup.string().required('Customer is required') }),
  officeName: Yup.string().required('Office name is required'),
  // officeType: Yup.string().required('Office type is required'),
  billingCity: Yup.string().required('City is required'),
  billingStreet: Yup.string().required('Street address is required'),
  billingContactAddress: Yup.string().required('Billing contact is required'),
  billingState: Yup.number()
    .required('Billing state is required') // Ensures field is not empty
    .test(
      'is-not-zero',
      'Billing state is required',
      (value) => value !== 0 // Check that the value is not 0
    ),

  contactState: Yup.number()
    .required('Contact state is required') // Ensures field is not empty
    .test(
      'is-not-zero',
      'Contact state is required',
      (value) => value !== 0 // Check that the value is not 0
    ),

  rebates: Yup.string().required('Rebates is required'),
  requirePurchaseOrder: Yup.string().required('Require purchase order is mandatory'),
  // invoiceTermID: Yup.string().required('Invoice due days is required'),
  // qboTermID: Yup.string().required('Invoice due days is required'),
  qboTermID: Yup.string().when([], (fields, schema) => {
    return isUATEnable ? schema.required('Invoice due days is required') : schema;
  }),
  officeDivisions: Yup.array().required('Office division is required'),
  contactZip: Yup.string()
    .matches(/(^$|^\d{5}$|^\d{9}$)/, { message: 'Invalid zip code' })
    .nullable()
    .required('Zip code is required'),
  contactPhone: Yup.string()
    .matches(/^[0-9]+$/, { excludeEmptyString: true, message: 'Invalid phone number' })
    .required('Contact phone number is required'),
  contactEmail: Yup.string().email('Invalid email').required('Contact email is required'),
  contactStreet: Yup.string().required('Street address is required'),
  contactCity: Yup.string().required('City is required'),
  billingZip: Yup.string()
    .matches(/(^$|^\d{5}$|^\d{9}$)/, { message: 'Invalid zip code' })
    .required('Zip code is required'),
  billingPhone: Yup.string()
    .matches(/^[0-9]+$/, { excludeEmptyString: true, message: 'Invalid phone number' })
    .required('Billing phone number is required'),
  billingEmail: Yup.string().email('Invalid email').required('Billing email is required'),
  builderCode: Yup.string().required('Builder code is required'),
  reportDeliveryTimeDays: Yup.number()
    .typeError('Report delivery time must be a number') // Custom type error message
    .positive('Report delivery time must be a positive number')
    .integer('Report delivery time must be an integer')
    .nullable()
    .test('required', 'Report delivery time is required', (value) => {
      // Check if the field is not undefined, null, or empty

      return String(value) !== undefined && String(value) !== '';
    }),
});

export const SaveOfficeComponent = ({ customerID, office, page }: SaveOfficeComponentProps) => {
  const navigate = useNavigate();
  const form = useReactHookForm({
    defaultValues: {
      isActive: true,
      ...office,
      // invoiceTermID: office?.invoiceTermID ? office.invoiceTermID : '',
      rebates: office?.rebates ? office?.rebates : '',
      // rebateCompanies: {
      //   utilRebateCompanyID: office?.rebateCompanies[0]?.utilRebateCompanyID ?? '',
      //   utilRebateCompanyName: office?.rebateCompanies[0]?.utilRebateCompanyName ?? '',
      // },
      officeCode: office?.officeCode ? office.officeCode : '',
      requirePurchaseOrder: office?.requirePurchaseOrder
        ? office.requirePurchaseOrder
        : office?.requirePurchaseOrder == undefined
          ? ''
          : false,
      billingState: office?.billingState ? office.billingState : 0,
      contactState: office?.contactState ? office?.contactState : 0,
      billingEmail: office?.billingEmail ? office.billingEmail : '',
      billingStreet: office?.billingStreet ? office.billingStreet : '',
      billingCity: office?.billingCity ? office.billingCity : '',
      // contactState: { stateID: office?.contactState ? office?.contactState : 0, stateName: '' },
      customer: { customerID: customerID ? customerID : '', customerName: '' },
      reportDeliveryTimeDays: office?.reportDeliveryTimeDays === 0 ? null : office?.reportDeliveryTimeDays,
      syncWithDux: !office?.syncWithDux ? false : true,
    },
    yupSchema: officeValidationSchema,
  });
  const [officeProps, setOfficeProps] = useState({
    customerID: office && office?.customerID,
    officeID: office && office?.officeID,
    requestedPage: 'office',
    planTempEntityID: 0,
  });
  const [rebateValue, setRebateValue] = useState<string>(office?.rebates ? office?.rebates : '');
  const [tempEntityID, setTempEntityID] = useState<any>('');
  const [selectedCustomerID, setCustomerID] = useState<number>(customerID ?? 0);
  const [isCustomerLoading, setCustomerLoading] = useState<boolean>(false);
  const [noteTempEntityID, setNoteTempEntityID] = useState<number>();
  const [openNoteDialog, setOpenNoteDialog] = useState(false);
  const { isLoading, mutateAsync } = useSaveData<Office>(
    office?.officeID ? officeApiKeyStore.updateOffice() : officeApiKeyStore.createOffice()
  );

  const {
    data: officeUserData,
    isLoading: isOfficeUserLoading,
    isFetching: isOfficeUserFetching,
  } = useLoadData<ApiResponse<any>>(
    customersApiKeyStore.getCustomer(officeProps?.customerID || customerID),
    !office?.officeID
  );

  const { data } = useLoadData<ApiResponse<Service[]>>(
    servicesApiKeyStore.getServiceMappingList(
      !office?.officeID ? form.getValues('customer.customerID') : 0,
      !office?.officeID ? 0 : office?.officeID,
      0,
      0,
      0
    ),
    !!officeProps?.customerID || !!customerID
  );

  useEffect(() => {
    if (officeUserData && !office?.officeID) {
      form.setValue('officeUsers', officeUserData?.data?.customerUsers);
    }
  }, [officeUserData]);

  useEffect(() => {
    if ((data && officeProps?.customerID) || (data && customerID)) {
      data?.data?.map((service: Service) => {
        if (service.serviceCode === 'Dry Run') {
          form.setValue('dryRunFee', service.price);
        } else if (service.serviceCode === 'Late Cancellation') {
          form.setValue('lateCancellationFee', service.price);
        } else if (service.serviceCode === 'Trip Charge') {
          form.setValue('tripCharge', service.price);
        }
      });
      // Toggle state to trigger re-render
    }
  }, [data, officeProps?.customerID, customerID]);

  const handleSubmit = async (office: Office) => {
    const filterData = data?.data
      ?.filter((d) => d.isNotReadyService)
      .map((service) => {
        if (service.serviceCode === 'Dry Run') {
          return {
            ...service,
            price: office?.dryRunFee,
          };
        } else if (service.serviceCode === 'Late Cancellation') {
          return {
            ...service,
            price: office?.lateCancellationFee,
          };
        } else if (service.serviceCode === 'Trip Charge') {
          return {
            ...service,
            price: office?.tripCharge,
          };
        }
        return service; // Return the service unchanged if no condition matches
      });

    await mutateAsync({
      data: {
        ...office,
        ...(noteTempEntityID && { noteTempEntityID }),
        ...(tempEntityID && { tempEntityID }),
        customerID: office.customer?.customerID,

        requirePurchaseOrder: JSON.parse(office.requirePurchaseOrder),
        officeContacts: office.officeContacts?.map((p) => p.contactsID),
        officeContracts: office.officeContracts?.map((p) => p.contractID),
        officeDivisions: office.officeDivisions?.map((p) => p.divisionID),
        qboCustomerID: office.customer?.qboCustomerID,
        officeServices: [...(office?.officeServices || []), ...(filterData || [])],
        qboTermID: !isUATEnable ? '3' : office?.qboTermID,
        officeContactList: office.officeContacts?.map((p) => {
          return {
            contactsID: p.contactsID,
            syncWithDux: office?.syncWithDux ? p.syncWithDux : false,
          };
        }),
      },
    }).then((data) => navigate(`/offices/details/${data?.data?.officeID}`));
    // navigate(`/offices`);
  };

  const handleOpenNoteDialog = () => {
    setOpenNoteDialog(true);
  };

  const handleCloseNoteDialog = (tempNoteEntityID?: number) => {
    setOpenNoteDialog(false);
    if (tempNoteEntityID) {
      setNoteTempEntityID(tempNoteEntityID);
    }
  };

  const handleTempEntityID = (fileTempEntityID?: any) => {
    if (fileTempEntityID) {
      setTempEntityID(fileTempEntityID);
    }
  };

  const handleCustomerLoading = (value: boolean) => {
    setCustomerLoading(value);
  };

  const handleSelecteCustomer = (customerID: number) => {
    setCustomerID(customerID);
    setOfficeProps((prevState) => ({
      ...prevState,
      customerID: customerID,
    }));
  };

  const handleRebateValue = (rebate: string) => {
    setRebateValue(rebate);
  };
  const { showSnackbar } = useSnackbar();

  const handleError = (error: any) => {
    const messages = parseErrorMessageRecursive(error);
    const errorCount = messages?.length > 1 ? ` | +${messages.length - 1} more` : '';

    let requiredMessages: any[] = [];
    let otherMessages: any[] = [];

    // Classify messages
    Array.isArray(messages) &&
      messages?.forEach((message) => {
        if (message.endsWith('is required')) {
          requiredMessages.push(message?.split(' is required')[0]?.trim());
        } else {
          otherMessages.push(message.trim());
        }
      });

    // Remove duplicates
    requiredMessages = Array.from(new Set(requiredMessages));
    otherMessages = Array.from(new Set(otherMessages));

    // Construct the final message
    const finalRequiredMessage = requiredMessages.length > 0 ? `${requiredMessages?.join(', ')} is required` : '';
    const finalOtherMessage = otherMessages?.join(', ');

    // Combine messages
    const finalMessage = [finalRequiredMessage, finalOtherMessage]?.filter(Boolean)?.join(', ');

    // Show the final formatted message
    showSnackbar(`Validation Error: ${finalMessage}`, 'error');
  };

  const handleFormSubmit = form.handleSubmit(handleSubmit as any, handleError);

  return (
    <>
      <ReactHookForm formName="OFFICE_FORM" onSubmit={() => { }} formContextProps={form} isBusy={isLoading}>
        <div>
          <Grid container spacing={2} style={styling.root}>
            <Grid item lg={3} sm={3} xl={3} xs={3}>
              <CustomerDropdown
                page={page}
                office={office}
                customerID={customerID}
                onCustomerLoading={handleCustomerLoading}
                onSelectCustomer={handleSelecteCustomer}
                onRebateValue={handleRebateValue}
              />
            </Grid>
            <Grid item lg={12} sm={12} xl={12} xs={12}>
              <OfficeInfoForm
                isCustomerLoading={isCustomerLoading}
                customerID={selectedCustomerID}
                rebateValue={rebateValue}
              />
            </Grid>
            <Grid item lg={12} sm={12} xl={12} xs={12}>
              <OfficeContactInfoForm />
            </Grid>
            <GuardedComponent requiredPermissions={[UserPermissionName.VIEW_CONTACT]}>
              <Grid item lg={12} sm={12} xl={12} xs={12}>
                <ContactsForm name="officeContacts" label="Contacts" page={page} />
              </Grid>
            </GuardedComponent>
            <GuardedComponent requiredPermissions={[UserPermissionName.VIEW_CONTRACT_OFFICE]}>
              <Grid item lg={12} sm={12} xl={12} xs={12}>
                <ContractsForm name="officeContracts" />
              </Grid>
            </GuardedComponent>
            <Grid item lg={12} sm={12} xl={12} xs={12}>
              <BillingDetailsForm />
            </Grid>
            <GuardedComponent requiredPermissions={[UserPermissionName.VIEW_SERVICE_OFFICE]}>
              <Grid item lg={12} sm={12} xl={12} xs={12}>
                <ServicesForm name="officeServices" page="office" id={!office?.officeID ? 0 : office?.officeID} />
              </Grid>
            </GuardedComponent>
            <Grid item lg={12} sm={12} xl={12} xs={12}>
              <UsersRoleComponent name="officeUsers" isLoading={isOfficeUserLoading || isOfficeUserFetching} />
            </Grid>
            <GuardedComponent requiredPermissions={[UserPermissionName.VIEW_FILES]}>
              <Grid item lg={12} sm={12} xl={12} xs={12}>
                <FileUpload
                  fileRequestArgs={{ ...(office ? { officeID: office.officeID } : { tempEntityID }) }}
                  handleTempEntityID={handleTempEntityID}
                />
              </Grid>
            </GuardedComponent>
            <Grid item lg={12} sm={12} xl={12} xs={12}>
              <FeaturesSetup openNoteDialog={handleOpenNoteDialog} />
            </Grid>
            <Grid item lg={12} sm={12} xl={12} xs={12}>
              <FormButtons officeID={office?.officeID} isLoading={isLoading} handleSubmit={handleFormSubmit} />
            </Grid>
          </Grid>
        </div>
      </ReactHookForm>
      <NotesDialog
        title="OFFICE"
        noteRequestArgs={{ ...(office ? { officeID: office.officeID } : { noteTempEntityID }) }}
        open={openNoteDialog}
        onClose={handleCloseNoteDialog}
      />
    </>
  );
};
